import {
  Activity,
  Answer,
  Assessment,
  ProgressKey,
  SectionProgress,
} from "@appally/collections/Assessment";
import {
  AssessmentSolution,
  AssessmentSolutionWithSolutions,
  isAssessmentSolutionVisibleToUser,
} from "@appally/collections/AssessmentSolution";
import { Solution } from "@appally/collections/Solution";
import { getSectionContent } from "@appally/hooks/useSectionContent";
import { auth, collections } from "@appally/lib/data/firebase";
import { checkStatus } from "@appally/lib/utils/checkStatus";
import { sendEmail } from "@appally/lib/utils/sendEmail";
import { progressKeyToSection } from "@appally/lib/utils/stringToSection";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { lowerCase, orderBy, startCase } from "lodash";
import { getCurrentUser, getMyHelperOrInviter } from "./user";

export const assessmentsWithAdditionalFields = (data: Assessment) => {
  data.status = checkStatus(data);
  data.has_changes = data.answers?.some(
    (answer) => answer.current_value !== answer.new_value && !!answer.new_value,
  );
  return data;
};

export const getAssessment = async (id?: string) => {
  try {
    if (!id) return null;
    const assessment = await getDoc(doc(collections.assessments, id));
    if (!assessment.exists()) return null;
    const data = assessment.data();
    return assessmentsWithAdditionalFields(data);
  } catch (e: any) {
    return null;
  }
};

export const getCurrentUsersAssessment = async () => {
  const id = auth.currentUser?.uid;
  return getAssessment(id);
};

export type UpdateMyAssessmentInput = {
  id: string;
  answers?: Pick<Answer, "current_value" | "question_id" | "section">[];
  activity?: Activity[];
  progressKey?: ProgressKey;
  status?: "started" | "complete";
  funding_strategy?: string;
  regenerate_analysis?: boolean;
};

export const updateAssessment = async (input: UpdateMyAssessmentInput) => {
  // Get the assessment
  const assessment = await getAssessment(input.id);
  if (!assessment)
    throw new Error("You don't have access to update this assessment");
  const activity = assessment.activity || [];
  const answers = assessment.answers || [];
  const progress = assessment.progress ?? {};

  // Set up the default section progress
  const defaultSectionProgress: SectionProgress = {
    started_at: Date.now(),
    last_updated_by: auth.currentUser?.uid ?? "",
  };

  // Loop through the answers and update them or create new
  input.answers?.forEach((newAnswer) => {
    const index = answers.findIndex(
      (answer) => answer.question_id === newAnswer.question_id,
    );
    if (index === -1) {
      answers.push({
        ...newAnswer,
        new_value: "",
        last_saved: Date.now(),
        status: "added",
      });
    } else {
      answers[index] = {
        ...newAnswer,
        new_value: "",
        last_saved: Date.now(),
        status: "changed",
      };
    }
  });

  // Add new activities
  if (input.activity) {
    activity.push(...input.activity);
  }

  // If a section is passed then we can update some progress stats
  if (input.progressKey) {
    const sectionProgress: SectionProgress =
      progress[input.progressKey] ?? defaultSectionProgress;
    const section = progressKeyToSection(input.progressKey);
    const content = section
      ? getSectionContent(section)
      : { title: startCase(lowerCase(input.progressKey)) };

    // Add an activity log if we've just completed the section for the first time
    if (input.status === "complete") {
      if (!sectionProgress.completed_at && !!content) {
        activity.push({
          title: `Completed <b>${content.title}</b>`,
          timestamp: Date.now(),
        });
      }
      if (!sectionProgress.started_at) {
        sectionProgress.started_at = Date.now();
      }
      sectionProgress.completed_at = Date.now();
    }

    // Add an activity log if they've just started
    if (
      input.status === "started" &&
      !sectionProgress.started_at &&
      !!content
    ) {
      activity.push({
        title: `Started <b>${content.title}</b>`,
        timestamp: Date.now(),
      });
    }

    // Update this section progress
    sectionProgress.last_updated_by = auth.currentUser?.uid ?? "";
    sectionProgress.updated_at = Date.now();
    progress[input.progressKey] = sectionProgress;

    // Update the assessment progress
    if (!progress.assessment && input.progressKey !== "assessment") {
      progress.assessment = {
        last_updated_by: auth.currentUser?.uid ?? "",
        updated_at: Date.now(),
        started_at: Date.now(),
      };
    }
  }

  // Set the update data
  const updateData: Partial<Assessment> = {
    answers,
    activity,
    progress,
    last_saved: Date.now(),
  };

  // Check if there were any changes answers
  if (input.regenerate_analysis !== undefined) {
    updateData.regenerate_analysis = input.regenerate_analysis;
  }

  // Update the funding strategy if it's passed
  if (input.funding_strategy) {
    updateData.funding_strategy = input.funding_strategy;
  }

  console.log("updateData", assessment.id, updateData);

  // Save the updates in the database
  await setDoc(doc(collections.assessments, assessment.id), updateData, {
    merge: true,
  });

  return {
    ...assessment,
    answers,
  };
};

export const confirmMyAssessmentHelper = async () => {
  const user = await getCurrentUser();
  if (!user) throw new Error("No user found");
  const assessment = await getCurrentUsersAssessment();
  if (!assessment) throw new Error("No assessment found");
  const helperId = user.team_invite;
  if (!helperId) throw new Error("No helper to confirm");

  // Save the updates in the database
  await setDoc(
    doc(collections.assessments, assessment.id),
    {
      assigned_helper_user: helperId,
      last_saved: Date.now(),
    },
    { merge: true },
  );

  // Save the updates in the database
  await setDoc(
    doc(collections.users, user.id),
    {
      team_invite: "",
      last_saved: Date.now(),
    },
    { merge: true },
  );

  // Send a confirmation email to the helper
  const helper = await getMyHelperOrInviter();
  if (!helper) {
    throw new Error("No helper to confirm, something went wrong");
  }
  const emailSent = await sendEmail(
    helper.email,
    "Invite to join your team accepted",
    `<p>Hi ${helper.firstName},</p>
              <p>${user.firstName} ${user.lastName} has accepted the invite to join your team.</p>
            <p>When you next login you will be able to access their account from your dashboard.</p>
            <p><a href="https://redux--send-it-to-alex.netlify.app/login/">Login</a></p>
            <br/>
            <p>Thanks,</p>
            <p>The App Ally team</p>
            `,
  );
  if (!emailSent) {
    console.error("Could not send the email");
  }

  // Add an activity log
  await updateAssessment({
    id: assessment.id,
    activity: [
      {
        title: `<b>${helper.firstName} ${helper.lastName}</b> assigned as designated helper`,
        timestamp: Date.now(),
      },
    ],
  });

  const newAssessment = await getCurrentUsersAssessment();
  if (!newAssessment) throw new Error("No assessment found");
  return newAssessment;
};

export const getAndVerifyAssessmentSolutions = async (id: string) => {
  // Get the current user's assessment solutions
  const assessment = await getAssessment(id);
  if (!assessment)
    throw new Error(
      "You don't have access to this assessment's recommendations",
    );
  const solutions = await getAssessmentSolutions(assessment.id);
  if (auth.currentUser?.uid !== assessment.assessment_user) return solutions;
  return solutions.filter(isAssessmentSolutionVisibleToUser);
};

export const getAssessmentSolutions = async (id: string) => {
  const thisDoc = await getDoc(doc(collections.assessment_solutions, id));
  if (!thisDoc.exists()) return [];
  const assessmentSolution = thisDoc.data();
  const solutions =
    await combineAssessmentSolutionWithSolutions(assessmentSolution);
  return solutions;
};

export const combineAssessmentSolutionWithSolutions = async (
  assessmentSolution: AssessmentSolution,
): Promise<AssessmentSolutionWithSolutions[]> => {
  const solutionIds = Object.keys(
    assessmentSolution.solutions,
  ) as Solution["id"][];

  const solutionDocs = await Promise.all(
    solutionIds.map((solutionId) =>
      getDoc(doc(collections.solutions, solutionId)),
    ),
  );
  const solutions = solutionDocs
    .filter((solutionDoc) => solutionDoc.exists())
    .map((solutionDoc) => solutionDoc.data());

  // Add the solutions to the objects
  return orderBy(
    solutionIds
      .map((solutionId) => {
        const solution = solutions.find((sol) => sol.id === solutionId);
        if (!solution) return null;
        const obj = assessmentSolution.solutions[solutionId];
        return {
          ...obj,
          id: assessmentSolution.id,
          solution,
        } satisfies AssessmentSolutionWithSolutions;
      })
      .filter((s) => !!s),
    ["final_score", "solution.solution_name"],
    ["desc", "asc"],
  );
};
